import React from 'react';
import { X, AlertCircle, AlertTriangle, Info, CheckCircle2, Lightbulb } from 'lucide-react';
import { ValidationIssue } from '../utils/architectureValidator';

interface ValidationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  issues: ValidationIssue[];
  onHighlightNodes?: (nodeIds: string[]) => void;
  onClearHighlight?: () => void;
}

const ValidationPanel: React.FC<ValidationPanelProps> = ({
  isOpen,
  onClose,
  issues,
  onHighlightNodes,
  onClearHighlight
}) => {
  if (!isOpen) return null;

  const errorCount = issues.filter(i => i.type === 'error').length;
  const warningCount = issues.filter(i => i.type === 'warning').length;
  const infoCount = issues.filter(i => i.type === 'info').length;

  const getIcon = (type: ValidationIssue['type']) => {
    switch (type) {
      case 'error':
        return <AlertCircle size={18} className="text-red-600" />;
      case 'warning':
        return <AlertTriangle size={18} className="text-yellow-600" />;
      case 'info':
        return <Info size={18} className="text-blue-600" />;
    }
  };

  const getColor = (type: ValidationIssue['type']) => {
    switch (type) {
      case 'error':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50';
      case 'info':
        return 'border-blue-200 bg-blue-50';
    }
  };

  return (
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white border-2 border-black rounded-lg shadow-xl z-[100] w-[600px] max-w-[90vw] max-h-[400px] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-black/10 flex justify-between items-center bg-gray-50">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={20} className="text-black" />
          <h2 className="font-bold text-black">Architecture Validation</h2>
          {issues.length === 0 && (
            <span className="text-xs text-green-600 font-medium">All checks passed!</span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-black transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Summary */}
      {issues.length > 0 && (
        <div className="p-4 border-b border-black/10 bg-gray-50 flex items-center gap-4 text-sm">
          {errorCount > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle size={14} />
              {errorCount} error{errorCount !== 1 ? 's' : ''}
            </span>
          )}
          {warningCount > 0 && (
            <span className="flex items-center gap-1 text-yellow-600">
              <AlertTriangle size={14} />
              {warningCount} warning{warningCount !== 1 ? 's' : ''}
            </span>
          )}
          {infoCount > 0 && (
            <span className="flex items-center gap-1 text-blue-600">
              <Info size={14} />
              {infoCount} suggestion{infoCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Issues List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {issues.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle2 size={48} className="mx-auto mb-4 text-green-600 opacity-50" />
            <p className="text-lg font-medium">No issues found</p>
            <p className="text-sm mt-2">Your architecture looks good!</p>
          </div>
        ) : (
          issues.map((issue, index) => (
            <div
              key={index}
              className={`border-2 rounded-lg p-3 ${getColor(issue.type)}`}
              onMouseEnter={() => {
                if (issue.nodeIds && issue.nodeIds.length > 0 && onHighlightNodes) {
                  onHighlightNodes(issue.nodeIds);
                }
              }}
              onMouseLeave={() => {
                if (onClearHighlight) {
                  onClearHighlight();
                }
              }}
            >
              <div className="flex items-start gap-2">
                {getIcon(issue.type)}
                <div className="flex-1">
                  <p className="text-sm font-medium text-black">{issue.message}</p>
                  {issue.suggestion && (
                    <div className="mt-2 flex items-start gap-2 text-xs text-gray-600">
                      <Lightbulb size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{issue.suggestion}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ValidationPanel;
